import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Paper,
  Divider,
  Radio,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { getInvoiceRecipients, syncInvoiceRecipients } from '../../store/actions';

const InvoiceRecipients = ({ tenantId }) => {
  const dispatch = useDispatch();

  const recipients = useSelector((state) => state.tenants.invoiceRecipients.data?.emails) || [];
  const primaryRecipientStore = useSelector((state) => state.tenants.invoiceRecipients.data?.primary);

  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [primaryRecipient, setPrimaryRecipient] = useState('');

  useEffect(() => {
    if (tenantId) {
      dispatch(getInvoiceRecipients(tenantId));
    }
  }, [dispatch, tenantId]);

  useEffect(() => {
    setPrimaryRecipient(primaryRecipientStore);
  }, [primaryRecipientStore])

  const handleAdd = () => {
    setError('');
    const trimmedEmail = newEmail.trim();

    if (!trimmedEmail) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Invalid email format');
      return;
    }
    if (recipients.some(r => r.email === trimmedEmail)) {
      setError('Recipient already exists');
      return;
    }

    const isFirst = recipients.length === 0;

    dispatch(syncInvoiceRecipients(tenantId, recipients, isFirst ? recipients[0] : primaryRecipient))
      .then(() => dispatch(getInvoiceRecipients(tenantId)));

    setNewEmail('');
  };

  const handleSetPrimary = (email) => {
    setPrimaryRecipient(email);
    dispatch(syncInvoiceRecipients(tenantId, recipients, email))
      .then(() => dispatch(getInvoiceRecipients(tenantId)));
  };

  const handleRemove = (emailToRemove) => {
    const recipientToRemove = recipients.find(r => r.email === emailToRemove);
    let updatedList = recipients.filter((r) => r.email !== emailToRemove);

    let newPrimary = primaryRecipient;
    if (recipientToRemove === primaryRecipient && updatedList.length > 0) {
      newPrimary = updatedList[0];
    }

    dispatch(syncInvoiceRecipients(tenantId, updatedList, newPrimary))
      .then(() => dispatch(getInvoiceRecipients(tenantId)));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fafafa' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Add New Recipient
        </Typography>
        <Box display="flex" gap={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. billing@company.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            error={!!error}
            helperText={error}
            InputProps={{
              startAdornment: <EmailIcon color="action" sx={{ mr: 1, fontSize: 20 }} />,
            }}
          />
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!newEmail}
            startIcon={<AddIcon />}
            sx={{ minWidth: '100px' }}
          >
            Add
          </Button>
        </Box>
      </Paper>

      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        Recipients <small>(Select star for Primary)</small>
      </Typography>

      <Paper variant="outlined">
        <List dense>
          {recipients.length === 0 ? (
            <Box p={3} textAlign="center" color="text.secondary">
              <PersonAddIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
              <Typography variant="body2">No recipients found.</Typography>
            </Box>
          ) : (
            recipients.map((recipient, index) => (
              <React.Fragment key={recipient}>
                {index > 0 && <Divider />}
                <ListItem>
                  <IconButton
                    onClick={() => handleSetPrimary(recipient)}
                    color={recipient.isPrimary ? "warning" : "default"}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    {recipient === primaryRecipient ? <StarIcon /> : <StarBorderIcon />}
                  </IconButton>

                  <ListItemText
                    primary={recipient}
                    secondary={recipient.isPrimary ? "Primary Billing Contact" : null}
                    primaryTypographyProps={{
                      fontWeight: recipient.isPrimary ? 700 : 500,
                      color: recipient.isPrimary ? 'primary.main' : 'inherit'
                    }}
                  />

                  <ListItemSecondaryAction>
                    <Tooltip title={recipient.isPrimary ? "Cannot delete primary" : "Remove"}>
                      <span>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemove(recipient)}
                          color="error"
                          disabled={recipient === primaryRecipient && recipients.length > 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default InvoiceRecipients;